import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { BoxOfferValidity } from "@/modules/boxOffer/entities/boxOfferTypes"
import { getRecurringPeriodInputLabel, getRecurringPeriodShortLabel } from "@/modules/boxOffer/utils/boxOfferUtils"
import { formatCurrency } from "@/modules/currency/currency"
import { CoachPaymentOfferType } from "@modules/coachOffers/entities/coachOfferTypes"
import { type CoachOfferPricingFormValues } from "@modules/coachOffers/types/coachOfferFormTypes"
import { word } from "@modules/core/utils/i18n"
import { PencilIcon, Trash2Icon } from "lucide-react"

type CoachOfferPricingOptionCardProps = {
  offer: CoachOfferPricingFormValues
  currency: string
  locale: string
  onEdit?: () => void
  onDelete?: () => void
  hideActions?: boolean
}

type DetailProps = {
  label: string
  value: string
}

function Detail({ label, value }: DetailProps) {
  return (
    <div className="flex flex-col gap-1">
      <Text fontStyle="LM" className="tracking-wide text-textOnSurface-subtle uppercase">
        {label}
      </Text>
      <Text fontStyle="TS" className="font-bold text-textOnSurface-default">
        {value}
      </Text>
    </div>
  )
}

function Pill({ label, value }: DetailProps) {
  return (
    <div className="flex flex-1 flex-col items-center gap-2.5">
      <Text fontStyle="LM" className="w-full tracking-wide text-textOnSurface-subtle uppercase">
        {label}
      </Text>
      <div className="flex w-full items-center justify-center rounded-[20px] border-2 border-surface-border-primary-default px-6 py-2">
        <Text fontStyle="TS" className="font-bold text-textOnSurface-default">
          {value}
        </Text>
      </div>
    </div>
  )
}

function getTypeLabel(type: CoachPaymentOfferType): string {
  switch (type) {
    case CoachPaymentOfferType.Subscription:
      return word("coach.offers.form.pricing.option.subscription")
    case CoachPaymentOfferType.TicketsBook:
      return word("coach.offers.form.pricing.option.ticketBook")
    case CoachPaymentOfferType.Free:
      return word("coach.offers.form.pricing.option.free")
  }
}

function getValidityLabel(values: CoachOfferPricingFormValues): string {
  if (values.validitySwitch === BoxOfferValidity.UNLIMITED) {
    return word("box.offer.form.infos.validity.types.UNLIMITED")
  }
  return `${values.validityPeriodMonths ? values.validityPeriodMonths.toString() : "0"} ${word("coach.offers.form.pricing.option.months")}`
}

export function CoachOfferPricingOptionCard({
  offer,
  currency,
  locale,
  onEdit,
  onDelete,
  hideActions,
}: CoachOfferPricingOptionCardProps) {
  return (
    <div className="flex flex-col gap-4 rounded-[20px] border border-surface-border-gray-default bg-surface-background-secondary px-5 py-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col">
          <Text fontStyle="TM" className="text-textOnSurface-default">
            {offer.title}
          </Text>
          {offer.isTrialSession ? (
            <div className="flex gap-1 text-textOnSurface-subtle">
              <Text fontStyle="BS">{getTypeLabel(offer.type)}</Text>
              <Text fontStyle="BS">{"·"}</Text>
              <Text fontStyle="BS">{word("box.offer.form.types.TRIAL")}</Text>
            </div>
          ) : (
            <Text fontStyle="BS" className="text-textOnSurface-subtle">
              {getTypeLabel(offer.type)}
            </Text>
          )}
        </div>
        {!hideActions && (
          <div className="flex items-center gap-2">
            <Button type="button" onClick={onDelete} variant="outline" size="iconMd">
              <Trash2Icon size={20} />
            </Button>
            <Button type="button" onClick={onEdit} variant="outline" size="iconMd">
              <PencilIcon size={20} />
            </Button>
          </div>
        )}
      </div>

      {/* Subscription */}
      {offer.type === CoachPaymentOfferType.Subscription && (
        <>
          <div className="flex items-start justify-between">
            {offer.price !== null && (
              <Detail
                label={word("global.price.label")}
                value={`${formatCurrency(offer.price, currency, locale)} ${getRecurringPeriodShortLabel(offer.recurringPeriod)}`}
              />
            )}
            {offer.recurringPeriod !== null && (
              <Detail
                label={word("coach.offers.form.pricing.option.paymentFrequency")}
                value={getRecurringPeriodInputLabel(offer.recurringPeriod)}
              />
            )}
          </div>
          <div className="flex items-end gap-2">
            {offer.minimumCommitmentPeriod !== null && (
              <Pill
                label={word("coach.offers.form.pricing.option.commitmentPeriod")}
                value={`${offer.minimumCommitmentPeriod} ${word("coach.offers.form.pricing.option.months")}`}
              />
            )}
            {offer.renewable !== null && (
              <Pill
                label={word("coach.offers.form.pricing.option.autoRenewal")}
                value={
                  offer.renewable
                    ? word("box.programmation.form.repetition.on.label")
                    : word("box.programmation.form.repetition.off.label")
                }
              />
            )}
          </div>
        </>
      )}

      {/* TicketsBook */}
      {offer.type === CoachPaymentOfferType.TicketsBook && (
        <>
          {offer.price !== null && (
            <Detail label={word("global.price.label")} value={formatCurrency(offer.price, currency, locale)} />
          )}
          <div className="flex items-end gap-2">
            {offer.count !== null && (
              <Pill label={word("coach.offers.form.pricing.option.sessionCount")} value={offer.count.toString()} />
            )}
            <Pill label={word("coach.offers.form.pricing.option.validityPeriod")} value={getValidityLabel(offer)} />
          </div>
        </>
      )}

      {/* Free */}
      {offer.type === CoachPaymentOfferType.Free && (
        <>
          <Detail label={word("global.price.label")} value={word("coach.offers.form.pricing.option.free")} />
          <div className="flex items-end gap-2">
            {offer.count !== null && (
              <Pill label={word("coach.offers.form.pricing.option.sessionCount")} value={offer.count.toString()} />
            )}
            <Pill label={word("coach.offers.form.pricing.option.validityPeriod")} value={getValidityLabel(offer)} />
          </div>
        </>
      )}
    </div>
  )
}
